import { ViewModel } from "mvvm/ViewModel";
import { PageViewModel } from "./PageViewModel";
import { JobFeedback, JobInfo, JobOutput } from "../../../../services/NLIJobService";
import { FeedbackViewModel } from "./FeedbackViewModel";
import { TagViewModel } from "./TagViewModel";
import { notification } from "antd";
import { CallLambda } from "redux/actions/CallLambda";

export type JobOutputState = Readonly<
    { status: "not-loaded"|"loading" } |
    { status: "loaded", output: JobOutput } |
    { status: "load-failed", error: string }>;

export class JobViewModel extends ViewModel {

    constructor(public readonly page: PageViewModel, info: JobInfo) {
        super();
        this.jobID = info.jobID;
        this._startTime = info.startTime;
        this._endTime = info.endTime;
        this._alias = info.alias;
        this._question = info.question;
        this._status = info.status;
        this._statusMessage = info.statusMessage;
        this._outputState = { status: "not-loaded" };
        this._feedback = info.feedback;
        this._tags = info.tags;
        this.feedbackUpViewModel = new FeedbackViewModel(this, "up");
        this.feedbackDownViewModel = new FeedbackViewModel(this, "down");
        this.tagViewModel = new TagViewModel(this);
        this._replyTarget = info.replyTarget;
        this._scheduleTime = info.scheduleTime;
        this._scheduleType = info.scheduleType;
        this.updateExecutionTime();
    }

    readonly jobID: string;
    private _startTime: number;
    private _endTime?: number;
    private _question: string;
    readonly feedbackUpViewModel: FeedbackViewModel;
    readonly feedbackDownViewModel: FeedbackViewModel;
    readonly tagViewModel: TagViewModel;
    private _replyTarget?: string;
    private _alias: string;
    private _status: string;
    private _statusMessage?: string;
    private _outputState: JobOutputState;
    private _isSelected = false;
    private _executionTime: string = "0s";
    private _showSQL = false;
    private _canShowSQL = false;
    private _feedback?: JobFeedback;
    private _tags: string[];
    private _scheduleTime?: string;
    private _scheduleType?: string;

    get scheduleTime() {
        return this._scheduleTime;
    }

    get scheduleType() {
        return this._scheduleType;
    }

    get status() {
        return this._status;
    }

    set status(value: string) {
        if (value !== this._status) {
            this._status = value;
            if (value === "RUNNING") {
                this._outputState = { status: "not-loaded" };
            }
            this.updateViews();
        }
    }

    get statusMessage() {
        return this._statusMessage;
    }

    private set statusMessage(value: string|undefined) {
        if (value !== this._statusMessage) {
            this._statusMessage = value;
            this.updateViews();
        }
    }

    get feedback() {
        return this._feedback;
    }

    set feedback(value: JobFeedback|undefined) {
        this._feedback = value;
        this.updateViews();
    }

    get tags() {
        return this._tags;
    }

    set tags(value: string[]) {
        this._tags = value;
        this.updateViews();
    }

    get outputState() {
        return this._outputState;
    }

    private set outputState(value: JobOutputState) {
        if (value !== this._outputState) {
            this._outputState = value;
            this.updateViews();
        }
    }

    get isSelected() {
        return this._isSelected;
    }

    set isSelected(value: boolean) {
        if (value !== this._isSelected) {
            this._isSelected = value;
            if (value) {
                if (this.page.selectedJob !== this) {
                    this.page.selectedJob = this;
                }
                if (this.status !== "RUNNING") {
                    this.loadOutput();
                }
            } else {
                if (this.page.selectedJob === this) {
                    this.page.selectedJob = undefined;
                }
            }
        }
    }

    get executionTime() {
        return this._executionTime;
    }

    updateExecutionTime(serverTime?: number) {
        if (this.endTime && this.status !== "RUNNING") {
            // Completed job - use actual execution time
            const executionTimeMs = this.endTime - this.startTime;
            const executionTimeSec = Math.round(executionTimeMs / 1000);
            this._executionTime = executionTimeSec < 60 ? `${executionTimeSec}s` :
                executionTimeSec < 3600 ? `${Math.floor(executionTimeSec / 60)}m` :
                `${Math.floor(executionTimeSec / 3600)}h`;
        } else if (this.status === "RUNNING" && serverTime) {
            // Running job - show elapsed time so far
            const elapsedTimeMs = serverTime - this.startTime;
            const elapsedTimeSec = Math.round(elapsedTimeMs / 1000);
            this._executionTime = elapsedTimeSec < 60 ? `${elapsedTimeSec}s` :
                elapsedTimeSec < 3600 ? `${Math.floor(elapsedTimeSec / 60)}m` :
                `${Math.floor(elapsedTimeSec / 3600)}h`;
        } else if (this.status === "RUNNING") {
            this._executionTime = "0s";
        } else {
            this._executionTime = "0s";
        }
        this.updateViews();
    }

    updateStatus(status: string, alias: string, statusMessage?: string, endTime?: number) {
        if (status !== this.status) {
            this.page.invalidateJobList();
        }
        this.status = status;
        this.statusMessage = statusMessage;
        this.alias = alias;
        if (endTime !== undefined) {
            this._endTime = endTime;
        }
        this.updateExecutionTime();

        if (this.isSelected && status !== "RUNNING") {
            this.loadOutput();
        }
    }

    get question() {
        return this._question;
    }

    get startTime() {
        return this._startTime;
    }

    get endTime() {
        return this._endTime;
    }

    get replyTarget() {
        return this._replyTarget;
    }

    async loadOutput() {
        if (this.outputState.status === "not-loaded") {
            try {
                this.outputState = { status: "loading" };
                // Load the full question from config if it's truncated
                await this.loadFullQuestion();
                const output = await this.page.jobService.loadJobOutput(this.jobID);
                this.canShowSQL = !!output.Response && !!output.Data;
                // Default to Data tab if there's data
                if (output.Data && output.Response) {
                    this._showSQL = true;
                }
                this.outputState = { status: "loaded", output };
            } catch (error) {
                this.outputState = { status: "load-failed", error: this.formatError(error) };
            }
        }
    }

    private async loadFullQuestion() {
        // If question is truncated (200 chars or less), load full question from config
        if (this._question.length <= 200) {
            try {
                const payload = JSON.stringify({
                    "model": this.page.modelAlias,
                    "jobID": this.jobID,
                });
                const response = await this.page.jobService.dispatch(
                    CallLambda(this.page.jobService.session, "jobLoader", payload)
                );
                if (response.status === 200) {
                    const config = JSON.parse(response.data.config);
                    const fullQuestion = config.parameters?.Request || config.parameters?.Memory || this._question;
                    if (fullQuestion && fullQuestion.length > this._question.length) {
                        this._question = fullQuestion;
                        this.updateViews();
                    }
                }
            } catch (error) {
                // Silently fail - use truncated question if config load fails
            }
        }
    }

    get alias() {
        return this._alias;
    }

    set alias(value: string) {
        if (value !== this._alias) {
            this._alias = value;
            this.updateViews();
        }
    }

    get canShowSQL() {
        return this._canShowSQL;
    }

    private set canShowSQL(value: boolean) {
        if (value !== this._canShowSQL) {
            this._canShowSQL = value;
            if (!value) {
                this._showSQL = false;
            }
            this.updateViews();
        }
    }

    get showSQL() {
        return this._showSQL;
    }

    set showSQL(value: boolean) {
        if (value !== this._showSQL) {
            this._showSQL = value;
            this.updateViews();
        }
    }

    get canDownloadOutput() {
        return this.outputState.status === "loaded" && (!!this.outputState.output.Response || !!this.outputState.output.Data || !!this.outputState.output.Error || !!this.outputState.output.ResponsePdf);
    }

    downloadOutput() {
        if (this.outputState.status === "loaded") {
            const output = this.outputState.output;
            let file  = this.showSQL ? (output.Error || output.Data || output.Response) : output.Response;

            if (file) {
                this.page.jobService.download(this.jobID, file.name, file.extension);
            }
        }
    }

    async runJob(memory: string) {
        this.page.isSubmitting = true;
        const jobInfo = await this.page.jobService.createJob(this.page.modelAlias, this.question, '', '', memory);
        this.page.addJob(jobInfo);
        this.page.isSubmitting = false;
    }

    async deleteJob() {
        this.page.isSubmitting = true;
        try {
            await this.page.jobService.deleteJob(this.jobID);
            this.page.removeJob(this);
            notification.success({
                message: this.replyTarget ? 'Request deleted' : 'Stream deleted',
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to delete',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        } finally {
            this.page.isSubmitting = false;
        }
    }

    private areTagsEqual(a: string[], b: string[]) {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    }

    private isFeedbackEqual(a?: JobFeedback, b?: JobFeedback) {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.rating === b.rating && a.comment === b.comment;
    }

    updateFromJobInfo(info: JobInfo) {
        let fieldsChanged = false;

        if (info.question !== this._question) {
            this._question = info.question;
            fieldsChanged = true;
        }

        if (info.startTime !== this._startTime) {
            this._startTime = info.startTime;
            fieldsChanged = true;
        }

        if (info.endTime !== this._endTime) {
            this._endTime = info.endTime;
            fieldsChanged = true;
        }

        if (info.replyTarget !== this._replyTarget) {
            this._replyTarget = info.replyTarget;
            fieldsChanged = true;
        }

        if (!this.areTagsEqual(this._tags, info.tags)) {
            this.tags = info.tags;
        }

        if (!this.isFeedbackEqual(this._feedback, info.feedback)) {
            this.feedback = info.feedback;
        }

        if (info.scheduleTime !== this._scheduleTime) {
            this._scheduleTime = info.scheduleTime;
            fieldsChanged = true;
        }

        if (info.scheduleType !== this._scheduleType) {
            this._scheduleType = info.scheduleType;
            fieldsChanged = true;
        }

        this.updateStatus(info.status, info.alias, info.statusMessage, info.endTime);

        if (fieldsChanged) {
            this.updateViews();
        }
    }
}